import type { Editor } from "@tiptap/react";
import { DOMParser as PmDOMParser, type Slice } from "@tiptap/pm/model";
import { counselDraftMarkdownToHtml } from "../formatCounselPanelHtml";
import { sanitizeAiHtml } from "../sanitizeHtml";
import { ensureParagraphIds } from "../draftParagraphUtils";
import {
  isInsideOrderedList,
  markImportedOrderedListsInRoot,
  preparePastedOrderedListSlice,
} from "./listNumbering";
import { structureLegalNumbering } from "./wordPaste/structureLegalNumbering";
import { stripMarkersFromListItemsInRoot } from "./wordPaste/structureListRuns";

function stripCounselSectionWrappers(html: string): string {
  return html.replace(
    /<section\b[^>]*\bdata-lexgat-counsel\b[^>]*>([\s\S]*?)<\/section>/gi,
    "$1",
  );
}

function counselDraftHtmlToRoot(html: string): HTMLElement | null {
  const parser = new DOMParser();
  const doc = parser.parseFromString(
    `<div id="counsel-draft-root">${html}</div>`,
    "text/html",
  );
  return doc.getElementById("counsel-draft-root");
}

/**
 * Normalize LexCounsel AI draft HTML for editor insertion: markdown → lists,
 * structure manual numbering, strip duplicate markers, mark independent lists.
 */
export function normalizeCounselDraftHtml(raw: string): string {
  let html = raw.trim();
  if (!html) return "";

  html = stripCounselSectionWrappers(html);

  if (!/<[a-z][\s\S]*>/i.test(html)) {
    html = counselDraftMarkdownToHtml(html);
  } else {
    html = html.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
  }

  html = sanitizeAiHtml(html);
  if (!html.trim()) return "";

  const root = counselDraftHtmlToRoot(html);
  if (!root) return html;

  structureLegalNumbering(root, new Map());
  stripMarkersFromListItemsInRoot(root);
  markImportedOrderedListsInRoot(root);

  return root.innerHTML.trim();
}

export function getCounselDraftSlice(
  editor: Editor,
  html: string,
  options: { adoptSurroundingNumbering?: boolean } = {},
): Slice {
  const normalized = normalizeCounselDraftHtml(html);
  const dom = document.createElement("div");
  dom.innerHTML = normalized || "<p></p>";
  const parsed = PmDOMParser.fromSchema(editor.schema).parseSlice(dom);
  const adoptSurroundingNumbering =
    options.adoptSurroundingNumbering ??
    isInsideOrderedList(editor.state.selection.$from);

  return preparePastedOrderedListSlice(parsed, { adoptSurroundingNumbering });
}

/** Insert normalized counsel draft HTML at the current selection. */
export function insertCounselDraftHtml(editor: Editor, html: string): boolean {
  const slice = getCounselDraftSlice(editor, html);
  if (!slice.content.size) return false;

  editor.view.dispatch(editor.state.tr.replaceSelection(slice));
  ensureParagraphIds(editor);
  return true;
}
